import React, { SyntheticEvent, KeyboardEvent, useState, useRef } from 'react';
import { IMaskInput } from 'react-imask';
import cx from 'classnames';

import './input.css';
import { getInputBorderAndPadding } from './helpers';

interface PercentInputProps {
    /**
     * Optional classes to add to the input
     */
    addClasses?: string;
    /**
     * If true, treat `value` of 1 as 100%
     * @default false
     */
    asFraction?: boolean;
    /**
     * Optional class config which will override the default classes on the input
     */
    classConfig?: InputClassConfig;
    /**
     * Optional decimal places to allow in input
     * @default 2
     */
    decimalPlaces?: number;
    /**
     * Current state of the input. Although this can be undefined, you should always manage it with
     * useState in the parent and ensure the type signature of the state variable is
     * `number | undefined`
     */
    value?: number;
    /**
     * Optional onBlur handler, passed directly to <input>
     */
    onBlur?(e: SyntheticEvent): void;
    /**
     * A method to call when the input changes. It will be given the numeric value (not the
     * formatted string). This should always call the setState function provided by useState in the
     * parent.
     * @param value - next value of the input
     */
    onChange(value: number): void;
    /**
     * Optional onFocus handler, passed directly to <input>
     */
    onFocus?(e: SyntheticEvent): void;
    /**
     * Optional max value - always defined as relative to 100 being 100%
     * @default 100
     */
    maxPercent?: number;
    /**
     * Optional min value - always defined as relative to 100 being 100%
     * @default 0
     */
    minPercent?: number;
    /**
     * Padding to apply to the input; defaults to "small"
     */
    padding?: 'small' | 'large';

    /**
     * Optional width
     */
    width?: number;
}

/**
 * An input that is set up to work with percentages. The value is expected to be scaled so that
 * `value` of `100` represents 100%. Accordingly, the default max of 100 represents 100%.
 * @param props - see {@link PercentInputProps}
 */
export const PercentInput = (props: PercentInputProps): JSX.Element => {
    const {
        addClasses,
        classConfig,
        asFraction = false,
        value,
        maxPercent = 100,
        minPercent = 0,
        onBlur,
        onChange,
        onFocus,
        padding = 'small',
        width,
    } = props;
    const [hasFocus, setHasFocus] = useState(false);
    const borderAndPadding = getInputBorderAndPadding({
        classConfig,
        hasFocus,
        padding,
    });
    const classNames = cx(borderAndPadding, addClasses);
    const decimalPlaces = props.decimalPlaces ?? 2;
    const inputRef = useRef<{ element: HTMLInputElement }>(null);

    // if (asFraction) {
    //     decimalPlaces += 2;
    // }

    let inputValue = value ?? 0;

    if (value !== undefined) {
        if (asFraction) {
            inputValue = value * 100;
        }
    }

    return (
        <IMaskInput
            ref={inputRef}
            className={classNames}
            mask={[
                {
                    mask: 'num%',
                    lazy: false,
                    blocks: {
                        num: {
                            mask: Number,
                            scale: decimalPlaces,
                            min: minPercent,
                            max: maxPercent,
                            radix: '.',
                        },
                    },
                },
            ]}
            onAccept={(nextValue: string): void => {
                const valueNumber = parseFloat(nextValue);

                if (!Number.isNaN(valueNumber)) {
                    if (asFraction) {
                        onChange(valueNumber / 100);
                    } else {
                        onChange(valueNumber);
                    }
                }
            }}
            onBlur={(e: SyntheticEvent): void => {
                if (onBlur) {
                    onBlur(e);
                }

                setHasFocus(false);
            }}
            onFocus={(e: SyntheticEvent): void => {
                if (onFocus) {
                    onFocus(e);
                }

                setHasFocus(true);
            }}
            onKeyPress={(e: KeyboardEvent<HTMLElement>): void => {
                if (e.key === 'Enter') {
                    if (inputRef.current) {
                        inputRef.current.element.blur();
                    }
                }
            }}
            style={{
                textAlign: 'end',
                width,
            }}
            unmask={true}
            value={inputValue.toString()}
        />
    );
};
